/**
 * Alignment Map filters, layout and open sidebar <-> URL strings (for `useUrlState`).
 *
 * They lived in component state, so "View full details" on an OKR and then Back reset the
 * map to the current quarter, all types, default layout, sidebar closed.
 *
 * Pure functions only (no React, no services) so the round trip is unit-testable.
 */

import type { AlignmentFilters } from '../components/okr/AlignmentMapFilters';
import type { LayoutOptions } from '../components/okr/LayoutControls';
import { decodeUrlList, encodeUrlList } from './urlList';

export const ALL_OKR_TYPES = ['company', 'department', 'team', 'individual'] as const;
/** `types` is written as this when every type is unticked; an empty value would mean "default". */
const NO_TYPES = 'none';

export const ALIGNMENT_URL_DEFAULTS = {
  types: ALL_OKR_TYPES.join(','),
  statuses: '',
  q: '',
  pmin: '0',
  pmax: '100',
  depts: '',
  period: 'current-quarter',
  from: '',
  to: '',
  dir: 'TB',
  nodesp: '50',
  ranksp: '120',
  minimap: '1',
  okr: '',
};

export type AlignmentUrlState = typeof ALIGNMENT_URL_DEFAULTS;

export const ALIGNMENT_URL_ALLOWED = {
  dir: ['TB', 'LR'],
  minimap: ['0', '1'],
} as const;

const splitList = (raw: string): string[] => raw.split(',').map((v) => v.trim()).filter(Boolean);

const toInt = (raw: string, fallback: number, min: number, max: number): number => {
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) ? Math.min(max, Math.max(min, n)) : fallback;
};

const toDate = (raw: string): Date | null => {
  if (!raw) return null;
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
};

export function urlToAlignmentFilters(
  s: AlignmentUrlState,
  currentQuarter: { startDate: Date; endDate: Date }
): AlignmentFilters {
  const types =
    s.types === NO_TYPES
      ? []
      : splitList(s.types).filter((t) => (ALL_OKR_TYPES as readonly string[]).includes(t));

  let startDate: Date | null;
  let endDate: Date | null;
  if (s.period === 'all') {
    startDate = null;
    endDate = null;
  } else if (s.period === 'current-quarter' && !s.from && !s.to) {
    startDate = currentQuarter.startDate;
    endDate = currentQuarter.endDate;
  } else {
    startDate = toDate(s.from);
    endDate = toDate(s.to);
  }

  return {
    types: new Set(types),
    statuses: new Set(splitList(s.statuses)),
    search: s.q,
    progressMin: toInt(s.pmin, 0, 0, 100),
    progressMax: toInt(s.pmax, 100, 0, 100),
    departments: new Set(decodeUrlList(s.depts)),
    timePeriod: { startDate, endDate, preset: s.period || 'current-quarter' },
  };
}

export function alignmentFiltersToUrl(f: AlignmentFilters): Partial<AlignmentUrlState> {
  const types = ALL_OKR_TYPES.filter((t) => f.types.has(t));
  const isCurrentQuarter = f.timePeriod.preset === 'current-quarter';
  return {
    types: types.length === 0 ? NO_TYPES : types.join(','),
    statuses: [...f.statuses].join(','),
    q: f.search,
    pmin: String(f.progressMin),
    pmax: String(f.progressMax),
    depts: encodeUrlList([...f.departments]),
    period: f.timePeriod.preset,
    // The current quarter is recomputed on load; any other period keeps its exact dates.
    from: !isCurrentQuarter && f.timePeriod.startDate ? f.timePeriod.startDate.toISOString() : '',
    to: !isCurrentQuarter && f.timePeriod.endDate ? f.timePeriod.endDate.toISOString() : '',
  };
}

export function urlToLayoutOptions(s: AlignmentUrlState): LayoutOptions {
  return {
    direction: s.dir === 'LR' ? 'LR' : 'TB',
    nodeSpacing: toInt(s.nodesp, 50, 0, 1000),
    rankSpacing: toInt(s.ranksp, 120, 0, 1000),
    showMiniMap: s.minimap !== '0',
  };
}

export function layoutOptionsToUrl(o: LayoutOptions): Partial<AlignmentUrlState> {
  return {
    dir: o.direction,
    nodesp: String(o.nodeSpacing),
    ranksp: String(o.rankSpacing),
    minimap: o.showMiniMap ? '1' : '0',
  };
}
